import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import { useCreateTicketMutation } from '../store/api/ticketApi';
import { Toaster } from '../components/ui/Toaster';
import { Loader } from '../components/ui/Loader';

export function CreateTicketPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [createTicket, { isLoading, error }] = useCreateTicketMutation();

  const [formData, setFormData] = useState({
    title: '',
    priority: 'medium',
    category: 'numbers',
    content: '',
  });
  const [attachments, setAttachments] = useState<FileList | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createTicket({
        ...formData,
        attachments: attachments || undefined,
      }).unwrap();
      navigate('/tickets');
    } catch (err) {
      console.error('Failed to create ticket:', err);
    }
  };

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center animate-slide-up">
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-4">
              {t('tickets.create.title')}
            </h1>
            <p className="text-neutral-medium text-lg font-body">
              {t('tickets.create.subtitle')}
            </p>
          </div>

          {/* Form */}
          <div className="card-glass rounded-2xl shadow-2xl p-6 md:p-8 mb-6 animate-scale-in">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-neutral-light font-semibold mb-2">
                  {t('tickets.create.titleLabel')}
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
                  placeholder={t('tickets.create.titlePlaceholder')}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-neutral-light font-semibold mb-2">
                    {t('tickets.create.priorityLabel')}
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
                  >
                    <option value="low">{t('tickets.priority.low')}</option>
                    <option value="medium">{t('tickets.priority.medium')}</option>
                    <option value="high">{t('tickets.priority.high')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-neutral-light font-semibold mb-2">
                    {t('tickets.create.categoryLabel')}
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50"
                  >
                    <option value="numbers">{t('tickets.category.numbers')}</option>
                    <option value="proxies">{t('tickets.category.proxies')}</option>
                    <option value="payments">{t('tickets.category.payments')}</option>
                    <option value="other">{t('tickets.category.other')}</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-neutral-light font-semibold mb-2">
                  {t('tickets.create.contentLabel')}
                </label>
                <textarea
                  required
                  rows={6}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50 resize-none"
                  placeholder={t('tickets.create.contentPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-neutral-light font-semibold mb-2">
                  {t('tickets.create.attachmentsLabel')}
                </label>
                <input
                  id="file-upload"
                  type="file"
                  multiple
                  onChange={(e) => setAttachments(e.target.files)}
                  className="sr-only"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-brand-accent hover:bg-brand-accent/10 transition cursor-pointer"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                  {i18n.language.startsWith('ar') ? 'ارفع ملف' : 'Choose Files'}
                </label>
              </div>

              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
                  {'data' in (error as { data?: { message?: string } })
                    ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
                    : t('errors.network')}
                </div>
              )}

              <div className="flex gap-4">
                <Link
                  to="/tickets"
                  className="flex-1 px-6 py-3 rounded-xl border border-neutral/20 text-neutral-light font-semibold hover:bg-neutral/5 transition flex items-center justify-center"
                >
                  {t('tickets.create.cancel')}
                </Link>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 px-6 py-3 rounded-xl bg-brand-accent text-brand-primary font-semibold hover:shadow-lg hover:shadow-brand-accent/50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <Loader size="sm" />
                      {t('tickets.create.creating')}
                    </>
                  ) : (
                    t('tickets.create.submit')
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Back Button */}
          <div className="text-center">
            <Link
              to="/dashboard"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>
      <Toaster />
    </div>
  );
}
